'use client';
// Les pièces d'une NC : captures d'écran, photos, PDF. Coller (Ctrl/Cmd+V) une capture
// ou choisir un fichier ; la liste ne porte jamais le contenu, l'image se charge à l'ouverture.
import React from 'react';
import { I, ConfirmButton } from '@/components/ui';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
interface Piece { id: string; libelle: string; fichierNom: string; fichierMime: string; fichierTaille: number; deposeLe: string; deposePar: string; deposeParCle: string }

export const lireEnBase64 = (f: Blob) => new Promise<string>((ok, ko) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result || ''));
  r.onerror = () => ko(r.error);
  r.readAsDataURL(f);
});

export function PiecesNC({ nc, modifiable }: { nc: { ref: string; documentId: string; statut: string }; modifiable: boolean }) {
  const { showToast, session } = useGed();
  const [pieces, setPieces] = React.useState<Piece[] | null>(null);
  const [vue, setVue] = React.useState<{ piece: Piece; data: string } | null>(null);
  const [envoi, setEnvoi] = React.useState(false);
  const estRQ = session.roles.includes('rq');
  const base = `/api/ged/ged/nc/${nc.documentId}`;

  const [rev, setRev] = React.useState(0);
  const charger = () => setRev((n) => n + 1);
  React.useEffect(() => {
    let vivant = true;
    void fetch(`${base}/pieces`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { pieces: [] }))
      .catch(() => ({ pieces: [] }))
      .then((d: { pieces?: Piece[] }) => { if (vivant) setPieces(d.pieces || []); });
    return () => { vivant = false; };
  }, [base, rev]);

  const deposer = async (f: Blob, nom: string) => {
    setEnvoi(true);
    try {
      const data = await lireEnBase64(f);
      const r = await fetchEcriture(`${base}/pieces`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fichierNom: nom, fichierData: data }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(d?.error?.message || 'Pièce refusée.', 'erreur'); return; }
      showToast('Pièce jointe.');
      charger();
    } finally { setEnvoi(false); }
  };

  const ouvrir = async (p: Piece) => {
    const r = await fetch(`/api/ged/ged/nc/piece/${p.id}`, { credentials: 'include' });
    if (!r.ok) { showToast('Pièce illisible.', 'erreur'); return; }
    const d = await r.json() as { fichierData: string; fichierMime: string };
    setVue({ piece: p, data: `data:${d.fichierMime};base64,${d.fichierData}` });
  };

  const retirer = async (p: Piece) => {
    const r = await fetchEcriture(`/api/ged/ged/nc/piece/${p.id}`, { method: 'DELETE', credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { showToast(d?.error?.message || 'Retrait refusé.', 'erreur'); return; }
    if (vue?.piece.id === p.id) setVue(null);
    charger();
  };

  const peutRetirer = (p: Piece) => modifiable && (estRQ || p.deposeParCle === session.key);

  return (
    <div style={{ display: 'grid', gap: 6 }}
      onPaste={modifiable ? (e) => {
        const img = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
        if (img) { e.preventDefault(); void deposer(img, `capture-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`); }
      } : undefined}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
        Pièces jointes (captures d’écran, photos, PDF)
      </div>
      {pieces && !pieces.length && <span style={{ fontSize: 12.5, color: 'var(--text-faint, #98A2B3)' }}>Aucune pièce.</span>}
      {(pieces || []).map((p) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Bouton taille="sm" onClick={() => void ouvrir(p)} title={`${p.fichierNom} — ${p.deposePar}`}>
            <I n="eye" s={12} />{p.libelle}
          </Bouton>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{Math.max(1, Math.round(p.fichierTaille / 1024))} Ko · {p.deposePar}</span>
          {peutRetirer(p) && (
            <ConfirmButton label="" titre="Supprimer cette pièce" icon="trash" variant="danger"
              question={`Supprimer la pièce « ${p.libelle} » ? Elle ne se récupère pas.`} confirmer="Supprimer" onConfirm={() => retirer(p)} />
          )}
        </div>
      ))}
      {vue && (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: 8, background: 'var(--surface-sunken)' }}>
          {vue.data.startsWith('data:application/pdf')
            ? <object data={vue.data} type="application/pdf" style={{ width: '100%', height: 420 }} aria-label={vue.piece.libelle} />
            // eslint-disable-next-line @next/next/no-img-element
            : <img src={vue.data} alt={vue.piece.libelle} style={{ maxWidth: '100%', maxHeight: 460, display: 'block' }} />}
          <Bouton taille="sm" onClick={() => setVue(null)} style={{ marginTop: 6 }}>Fermer l’aperçu</Bouton>
        </div>
      )}
      {modifiable && (
        <label className="ged-btn" style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer' }}>
          <I n="plus" s={12} />{envoi ? 'Envoi…' : 'Joindre un fichier'}
          <input type="file" accept="image/*,application/pdf" hidden disabled={envoi}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void deposer(f, f.name); }} />
        </label>
      )}
      {modifiable && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Astuce : collez une capture d’écran (Ctrl/⌘ + V) dans cette zone.</span>}
    </div>
  );
}
